import React from 'react';
import type { SimpleCitation } from '../../types/qa';
import { safeCompetitiveIntelUrl } from '../../utils/competitiveIntelUrl';

// A version string is ``YYYY-MM-DD_<seq>`` — render a DAY-level "D Mon YYYY" so
// two versions in the same month stay distinguishable on the interval label.
const CHIP_MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function vstrShort(v?: string | null): string {
  if (!v) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (!m) return v;
  const mon = CHIP_MON[parseInt(m[2]!, 10) - 1] || '';
  return `${parseInt(m[3]!, 10)} ${mon} ${m[1]}`;
}

function shortVersionLabel(label: string): string {
  return label.replace(/\s+\([^)]*\)\s*$/, '').trim();
}

function compactVersionLabel(labels?: string[]): string | null {
  const populated = (labels || []).map((label) => label.trim()).filter(Boolean);
  if (populated.length === 0) return null;
  if (populated.length === 1) return shortVersionLabel(populated[0]!);

  const original = populated.filter((label) => /^Original(?:\s|\(|$)/i.test(label));
  const supplements = populated.filter((label) => /^Supplement(?:\s|\(|$)/i.test(label));
  if (original.length === 1 && supplements.length === populated.length - 1) {
    return `Original + ${supplements.length} supplement${supplements.length === 1 ? '' : 's'}`;
  }

  return `${shortVersionLabel(populated[0]!)} +${populated.length - 1}`;
}

/**
 * Citation chip — renders differently based on document type.
 * Label citations: colored by version_type (green=current, amber=historical, blue=draft)
 * SBA citations: teal coloring with section_id for highlighting
 *
 * Task B4: when the citation carries a ``drug_id`` (e.g. from
 * ``investigate_drugs`` multi-drug answers), the chip also shows a small
 * drug-name affordance so the user can tell which drug the citation
 * belongs to before clicking through.  ``drugNameById`` is an optional
 * lookup the host can pass in (search-results / compare modes already
 * have it in state); when no name is available we fall back to a
 * ``"Drug #42"`` label.  A future follow-up will lazy-fetch the
 * brand_name from the drug-summary endpoint for fda_letter /
 * regulatory_document modes where the drug isn't already in state.
 */
/**
 * What we RECORDED about this citation's evidence — never why the model found
 * it persuasive.
 *
 * Every line here is a fact already carried by the span: which sentences, which
 * section, which page. Asking the model to explain its own reasoning after the
 * fact is reconstruction, which is exactly what capture-at-source replaced. It
 * would be plausible, unfalsifiable and occasionally wrong, and one invented
 * rationale costs the reader their trust in every citation on the page.
 *
 * Returns null when there is no span, so a citation without one renders
 * exactly as it does today.
 */
/** A native title tooltip has to stay readable: list this many sentences, then
 *  count the rest. Six covers the overwhelming majority of citations whole. */
const MAX_HOVER_SENTENCES = 6;

/** Per-sentence clip. Shorter than the old single-quote budget of 140 because
 *  several now share the box; a regulatory sentence is identifiable well
 *  inside this. */
const MAX_HOVER_SENTENCE_CHARS = 110;

function evidenceTooltip(citation: SimpleCitation): string | null {
  const spans = citation.evidence_spans;
  if (!spans || spans.length === 0) return null;
  // `texts` is optional on purpose — a span written by a build older than
  // 4f3aaae0a has none, and chat snapshots outlive deploys. No sentences
  // means no claim to make, so the hover stays silent.
  const sentences = spans.reduce((n, sp) => n + (sp.texts?.length ?? 0), 0);
  if (sentences === 0) return null;
  const pages = Array.from(
    new Set(spans.map((sp) => sp.page).filter((pg): pg is number => pg != null)),
  );
  const where = citation.section_number
    ? `\u00a7${citation.section_number}`
    : (citation.section_title || '');
  const parts = [
    sentences === 1
      ? 'This answer is built on 1 sentence'
      : `This answer is built on ${sentences} sentences`,
    where ? `from ${where}` : null,
    pages.length === 1
      ? `on page ${pages[0]}`
      : pages.length > 1 ? `across pages ${pages.join(', ')}` : null,
  ].filter(Boolean);
  // EVERY cited sentence, not just the first.
  //
  // This printed `spans[0].texts[0]` \u2014 one sentence out of N \u2014 while the viewer
  // draws a box for EVERY span that resolves. Measured on staging 2026-10-07
  // (IBTROZI page 279): a chip saying "2 sentences" quoted the ILD sentence
  // while the box sat on the torsade sentence eight lines higher. Both were
  // genuinely cited, in two different chunks, and the matcher locates each
  // correctly \u2014 but the reader saw a quote and a box that disagreed and had no
  // way to tell they were siblings.
  //
  // It also made the highlight unverifiable by eye: "is the box around the
  // quoted sentence" has no answer when the chip quotes an arbitrary one of
  // several.
  const sentenceList = spans.flatMap((sp) => sp.texts ?? []);
  const shown = sentenceList.slice(0, MAX_HOVER_SENTENCES);
  const hidden = sentenceList.length - shown.length;

  const clip = (s: string) => (
    s.length > MAX_HOVER_SENTENCE_CHARS
      ? `${s.slice(0, MAX_HOVER_SENTENCE_CHARS)}\u2026`
      : s
  );
  // No numbering for a lone sentence \u2014 "1." implies a second one exists.
  const body = shown.length === 1
    ? `\u201c${clip(shown[0]!)}\u201d`
    : shown.map((t, i) => `${i + 1}. \u201c${clip(t)}\u201d`).join('\n');
  const more = hidden > 0 ? `\n+ ${hidden} more` : '';
  const quoted = body ? `\n\n${body}${more}` : '';
  return `${parts.join(' ')}.${quoted}`;
}

export const CitationChip: React.FC<{
  citation: SimpleCitation;
  index: number;
  onClick?: () => void;
  /** Optional drug_id → display name map for the drug-name badge. */
  drugNameById?: Record<number, string>;
}> = ({ citation, index, onClick, drugNameById }) => {
  const evidenceHint = evidenceTooltip(citation);
  // competitive_intel is the FIRST citation type with an EXTERNAL href (a
  // competitor's own website / press release) rather than an in-app viewer
  // route — render it as a real anchor (or a non-clickable chip when the
  // URL isn't safe) and return early, before any of the label/HA-specific
  // fields below (section_number, version_type, ...) are ever read — CI
  // citations don't carry them. When a safe URL IS rendered, the anchor's
  // own navigation is the only thing that should happen on click: we
  // deliberately do NOT wire the ``onClick`` host callback here (it would
  // double-open the same destination).
  if (citation.document_type === 'competitive_intel') {
    const safeUrl = safeCompetitiveIntelUrl(citation.external_url);
    const label = citation.document_title || citation.company_name || 'Source';
    const chipClass =
      'inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full transition-colors';
    if (safeUrl) {
      return (
        <a
          href={safeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${chipClass} bg-slate-100 text-slate-700 hover:bg-slate-200`}
          title={label}
        >
          <span className="font-medium">[{index + 1}]</span>
          <span className="truncate max-w-[150px]">{label}</span>
        </a>
      );
    }
    return (
      <span
        className={`${chipClass} bg-slate-100 text-slate-400 cursor-not-allowed`}
        title={label}
      >
        <span className="font-medium">[{index + 1}]</span>
        <span className="truncate max-w-[150px]">{label}</span>
      </span>
    );
  }

  const versionColors: Record<string, string> = {
    fda_current: 'bg-green-100 text-green-700 hover:bg-green-200',
    fda_historical: 'bg-amber-100 text-amber-700 hover:bg-amber-200',
    draft: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
    sba: 'bg-teal-100 text-teal-700 hover:bg-teal-200',
  };

  const colorClass = versionColors[citation.version_type ?? ''] || 'bg-slate-100 text-slate-700';

  // FDA-printed / bundle-local presentation number (SBA bundle-split
  // consumers, Task 8). ADDITIVE display metadata ONLY — ``citation.
  // section_number`` itself is never altered here; ``onClick`` (wired by
  // MessageBubble) and Drug Detail navigation keep dispatching on the
  // canonical value untouched. This chip just prefers the FDA-printed
  // number for what the USER sees when no section_title is available,
  // falling back to canonical for every non-bundle-split citation (local
  // absent/null).
  const displayNumber = citation.section_number_local || citation.section_number;

  // Show a small drug-name badge for label citations that carry a
  // drug_id (typical of multi-drug ``investigate_drugs`` answers).
  // Prefer the backend-resolved ``drug_name`` (brand, else generic), then
  // the host-provided ``drugNameById`` map, and only fall back to
  // ``Drug #<id>`` when neither resolves a name.
  const showDrugBadge =
    citation.document_type === 'label' && citation.drug_id != null;
  const drugLabel = showDrugBadge
    ? citation.drug_name
      ?? drugNameById?.[citation.drug_id as number]
      ?? `Drug #${citation.drug_id}`
    : null;

  // EU-historical chips carry the change INTERVAL so the reader can tell which
  // version pair a citation refers to before clicking (a section can change in
  // several intervals). ``to`` absent → the change lands in the current version.
  const intervalLabel =
    citation.document_type === 'eu_historical'
      ? `${vstrShort(citation.from_version) || '—'} → ${citation.to_version ? vstrShort(citation.to_version) : 'current'}`
      : citation.document_type === 'label_version_diff'
        ? `v${citation.from_version_number ?? '—'} → v${citation.to_version_number ?? '—'}`
        : null;

  const versionLabels = (citation.version_labels || [])
    .map((label) => label.trim())
    .filter(Boolean);
  const versionLabel = compactVersionLabel(versionLabels);

  // Tooltips keep complete version identity even when the visible badge is
  // compact. Legacy EU-history citations retain their raw change interval.
  // Bundle-split SBA chips surface the FDA-printed (local) number via
  // ``displayNumber`` (``section_number_local`` ?? ``section_number``) rather
  // than the internal ``SEG<N>[.x.y]`` token — section_number stays the
  // click-target identity.
  const tooltip = versionLabels.length > 0
    ? [
      citation.document_title?.trim()
        || citation.section_title
        || `Section ${displayNumber}`,
      ...versionLabels,
    ].join('\n')
    : intervalLabel
      ? `${citation.section_title || `Section ${displayNumber}`} · changed `
        + (citation.document_type === 'label_version_diff'
          ? intervalLabel
          : `${citation.from_version || '—'} → ${citation.to_version || 'current'}`)
      : `${citation.section_title || displayNumber} (${citation.version_type})`;

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full transition-colors ${colorClass}`}
      // The evidence hint REPLACES the routing tooltip when a span exists:
      // "built on 2 sentences from §4.4 on page 12" tells the reader something
      // about their answer, where the fallback only names the destination.
      title={evidenceHint ? `${evidenceHint}\n\n${tooltip}` : tooltip}
      data-has-evidence={evidenceHint ? 'true' : undefined}
    >
      <span className="font-medium">[{index + 1}]</span>
      {drugLabel && (
        <span className="inline-flex items-center px-1.5 rounded-full bg-white/60 text-[10px] font-medium text-slate-700">
          {drugLabel}
        </span>
      )}
      <span className="truncate max-w-[120px]">
        {citation.section_title || `Section ${displayNumber}`}
      </span>
      {intervalLabel && (
        <span className="inline-flex items-center px-1.5 rounded-full bg-white/60 text-[10px] font-medium tabular-nums text-slate-700">
          {intervalLabel}
        </span>
      )}
      {versionLabel && (
        <span className="inline-flex max-w-[150px] truncate items-center px-1.5 rounded-full bg-white/60 text-[10px] font-medium text-slate-700">
          {versionLabel}
        </span>
      )}
    </button>
  );
};
